import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { FormattedMessage, injectIntl } from "react-intl";
import API from "../../../utils/API";
import MesTable from "../../../components/Table/MesTable";
import tableCheckbox from "../../../components/Inputs/tableCheckbox";
import constants from "../../../utils/constants";
import useInfiniteScroll from "../../../components/common/useInfiniteScroll";
import Banner from "../../../components/MessageManager/Banner";
import NotificationModal from "../../../components/MessageManager/NotificationModal";
import remove from "../../../assets/icons/signage-plus/remove.svg";
import assign from "../../../assets/icons/message-manager/assign.svg";
import add from "../../../assets/icons/message-manager/add.svg";
import editIcon from '../../../assets/icons/table/edit.svg';
import trashIcon from '../../../assets/icons/table/trash.svg';
import FolderTree from "./FolderTree";
import AddNewFolder from "./AddNewFolder";
import AddFiles from "./AddFiles";
import EditFiles from "./EditFiles";
import '../SignagePlus.css';
import commonMethods from '../../../utils/common-methods';
import {
  getFolders,
  getFiles,
  addFolder,
  deleteFolder,
  addFile,
  deleteFile,
  updateFile
} from "./mockMediaLibraryApi";

function SignageMediaLibrary(props) {
  const navigate = useNavigate();
  const { user } = props;
  const permissionIds = user.permissions.map((permission) => permission.id);

  if (!(permissionIds.includes(constants.permissionIds.signagePlusOwner) || permissionIds.includes(constants.permissionIds.signagePlusTeacher))) {
    navigate("/forbidden");
  }

  const [selectedFiles, setSelectedFiles] = useState(null);
  const [mediasData, setMediasData] = useState([]);
  const [data, setData] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedIds, setSelectedIds] = useState({});
  const [sortByCol, setSortByCol] = useState('');
  const [sortByAsc, setSortByAsc] = useState();
  const [startIndexForTable, setStartIndexForTable] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [filesData, setFilesData] = useState([]);
  const [checkedKey, setCheckedKey] = useState([]);
  const [selectedKey, setSelectedKey] = useState("-1");
  const [folderTreeData, setFolderTreeData] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [totalSize, setTotalSize] = useState("0.0000 GB");
  const [selectedSize, setSelectedSize] = useState("0.0000 GB");
  const tableRef = useRef();
  const tableWrapperRef = useRef();
  const modalRef = useRef(null);
  const [notificationProps, setNotificationProps] = useState({
    message: "ML_REMOVE_TIPS",
    description: "ML_REMOVE_INFO",
    icon: remove,
    showSecondaryButton: true,
    primaryButtonText: "CONTINUE",
    className: "red",
  });

  const loadTable = async (map, resetSelection = true) => {
    map.data = {};
    map.ids = [];
    map.sortedIds = [];
    map.filteredIds = [];
    if (resetSelection) {
      map.selectedIds = {};
    }
    try {
      if (mediasData.length > 0) {
        mediasData.forEach((item) => {
          if (selectedKey === "-1" || selectedKey.includes(item.parentId)) {
            map.data[item.id] = {
              id: item.id,
              parentId: item.parentId,
              name: item.name,
              type: item.type,
              createdBy: `${item.createdBy?.firstName || ''} ${item.createdBy?.lastName || ''}`,
              createdAt: item.createdAt ? commonMethods.formatDate(item.createdAt) : '',
              size: `${(item.size / 1024).toFixed(2)} KB`, // size in KB,
              sizeInt: parseFloat(item.size) || 0,
              checked: false,
              content: item.content
            };
          }
        });
      }

      map.filteredIds = Object.keys(map.data);
      map.ids = Object.keys(map.data);
      setFilesData(map.data);
      setDataCount(map.ids.length);
      setSelectedIds(map.selectedIds);
      setTotalDataCount(map.ids.length);
      setDataLoaded(true);

    } catch (err) {
      setError(err.message);
      setDataLoaded(true);
    }
  };

  const closeModal = (modalId) => {
    const modal = document.getElementById(modalId);
    if (modal) {
      const closeButton = modal.querySelector(
        '.btn-close, [data-bs-dismiss="modal"]'
      );
      if (closeButton) {
        closeButton.click();
      }
    }
    fetchFolderTreeData();
  };

  const dataMap = useMemo(() => {
    const map = {
      data: {},
      ids: [],
      selectedIds: {},
      filteredIds: [],
      sortedIds: [],
    };
    loadTable(map);
    return map;
  }, []);

  const parseFolderData = (folderData) => {
    return folderData.map((node) => ({
      title: node.name,
      key: node.id,
    }));
  };

  const fetchFolderTreeData = async () => {
    const res = await API.getMedias();
    console.log('medias', res.data);
    if (res && res.data) {
      const folders = [];
      const medias = [];
      res.data.forEach((item) => {
        if(item.category === constants.SIGNAGE_MEDIA.MEDIA_CATEGORY.MEDIA) 
        {
          if(item.subcategory === constants.SIGNAGE_MEDIA.MEDIA_SUB_CATEGORY.FOLDER){
            folders.push({
              key: item.id,
              title: item.name,
              siteId: item.siteId,
              cmsId: item.cmsId,
            });
          }else if(item.subcategory === constants.SIGNAGE_MEDIA.MEDIA_SUB_CATEGORY.FILE){
            medias.push(item);
          }
        }
      });
      const totalSizeInKB = medias.reduce((sum, media) => sum + (media.size || 0), 0); 
      const totalSizeInGB = totalSizeInKB === 0 ? "0.0000 GB" : totalSizeInKB < 100
        ? "0.0001 GB" : `${(totalSizeInKB / (1024 * 1024 * 1024)).toFixed(4)} GB`;
      setTotalSize(totalSizeInGB);
      setFolderTreeData(folders);
      setMediasData(medias);
    }
  };

  useEffect(() => {
    modalRef.current = new window.bootstrap.Modal(
      document.getElementById("notificationModal")
    );
    fetchFolderTreeData();
  }, []);

  useEffect(() => {
    loadTable(dataMap);
  }, [selectedKey, mediasData]);

  useEffect(() => {
    if (selectedKey !== "-1" && selectedKey.length === 0) {
      setSelectedKey("-1");
    }
  }, [selectedKey]);

  useEffect(() => {
    if (selectedIds && Object.keys(selectedIds).length > 0) {
      const selectedSizeInKB = Object.values(filesData)
      .filter((item) => selectedIds[item.id]) 
      .reduce((sum, item) => sum + (item.sizeInt || 0), 0);

    const selectedSizeInGB =
      selectedSizeInKB === 0
        ? "0.0000 GB"
        : selectedSizeInKB < 100
        ? "0.0001 GB"
        : `${(selectedSizeInKB / (1024 * 1024 * 1024)).toFixed(4)} GB`;

        setSelectedSize(selectedSizeInGB);
    } else{
      setSelectedSize("0.0000 GB");
    }
  }, [selectedIds]);

  const handleDeleteFolder = async (key) => {
    const res = await API.deleteMediaFolder(key);
    if(res.status === 200){
      const updatedFolder = folderTreeData.filter((folder) => folder.key !== key);
      setFolderTreeData(updatedFolder);
      setSelectedKey("-1");
      setSuccess(<FormattedMessage id="DELETE_FOLDER_SUCCESS" />);
    }else{
      setError(<FormattedMessage id="DELETE_FOLDER_FAILED"  values={{ errorMsg: res.data.message }} />);
    }
  };

  const handleDeleteConfirm = (ids, e) => {
    try{
      if (e) e.stopPropagation();
      setNotificationProps({
        icon: remove,
        message: 'ML_REMOVE_TIPS',
        description: 'ML_REMOVE_INFO',
        showSecondaryButton: true,
        primaryButtonText: 'CONTINUE',
        className: 'red',
        onSubmit: async () => {
          try{
            let delFiles = {};
            ids.forEach((id) => {
              if (dataMap.data[id]) {
                delFiles[id] = {
                  type: dataMap.data[id].type,
                  content:dataMap.data[id].content,
                };
              }
            });

            const res = await API.deleteMediaFiles(delFiles);
            if (res.status == 200) {
              if (typeof fetchFolderTreeData === 'function') fetchFolderTreeData();
              if (typeof loadTable === 'function') loadTable(dataMap);
              setSuccess(<FormattedMessage id="REMOVE_FILE_SUCCESS" />);
            } else {
              setError(<FormattedMessage id="REMOVE_FILE_FAILED" />);
            }
          } catch (err) {
            setError(<FormattedMessage id="REMOVE_FILE_FAILED" />);
          }
          modalRef.current.hide();         
        }
      });
    } catch (err) {
      setError(<FormattedMessage id="REMOVE_FILE_FAILED" />);
    }
  };

  const handleMoveFilesConfirm = (selectedMediaIds, e) => {
    if (e) e.stopPropagation();
    try{
      setNotificationProps({
        icon: trashIcon,
        message: 'ARE_YOU_SURE',
        description: 'ML_MOVE_FILES_INFO',
        showSecondaryButton: true,
        primaryButtonText: 'CONTINUE',
        className: 'red',
        onSubmit: async () => {
         const folderName = checkedKey.length > 0 ? folderTreeData.find(folder => folder.key === checkedKey[0]).title : 'Selected Folder';
         try{
          const res = await API.moveMediaFiles({id: checkedKey.length > 0 ? checkedKey[0] : null, ids: selectedMediaIds});
          if (res.status === 200) {
            setSuccess(<FormattedMessage id="MOVE_FILES_SUCCESS" values={{ folderName: folderName }} />);
            if (typeof fetchFolderTreeData === 'function') fetchFolderTreeData();
            if (typeof loadTable === 'function') loadTable(dataMap);
          } else {
            setError(<FormattedMessage id="MOVE_FILES_FAILED" values={{ folderName: folderName }} />);
          }
         } catch (err) {
          setError(<FormattedMessage id="MOVE_FILES_FAILED" values={{ folderName: folderName }} />);
         }
          modalRef.current.hide();
        }
      });
    } catch (err) {
      const folderName = checkedKey.length > 0 ? folderTreeData.find(folder => folder.key === checkedKey[0]).title : 'Selected Folder';
      setError(<FormattedMessage id="MOVE_FILES_FAILED" values={{ folderName: folderName }} />);
    }
  }

  const triggerEditFolder = async (key) => {

    /*const updatedFolder = folderTreeData.filter((group) => group.key !== key);
    setFolderTreeData(updatedFolder);
    setSelectedKey("-1");*/
    const selectedFolderData = folderTreeData.find((folder) => folder.key === key);
    setSelectedFolder(selectedFolderData);
  };

  // Hook for infinite scrolling
  const { generateDataSlice, sortMap, setSortBy } = useInfiniteScroll({
    dataMap,
    setData,
    setStartIndexForTable,
    startIndexForTable,
    pageSize: 100,
    sortByCol,
    setSortByCol,
    sortByAsc,
    setSortByAsc,
    tableWrapperRef,
    searchValue,
    tableRef,
    rowHeight: 48,
  });

  useEffect(() => {
    if (dataLoaded) {
      if (searchValue !== "") {
        tableRef.current.search(dataMap);
      } else {
        sortMap(dataMap);
        setData(generateDataSlice(dataMap));
      }
    }
  }, [sortByCol, sortByAsc, dataLoaded, dataMap, filesData]);

  const handleEditClick = (e, id) => {
    e.preventDefault();
    let clickedFiles = dataMap.data[id];
    setSelectedFiles(clickedFiles);
  };

  const tableColumnArray = [
    {
      Header: tableCheckbox,
      id: "checkbox",
      accessor: "checked",
      Cell: tableCheckbox,
      isVisible: true,
      width: 63,
      maxWidth: 63,
    },
    {
      Header: "MEDIA_NAME",
      id: "name",
      accessor: "name",
      isVisible: true,
    },
    {
      Header: "MEDIA_TYPE",
      id: "type",
      accessor: "type",
      isVisible: true,
      width: 90,
    }, 
    {
      Header: "UPLOADED_BY",
      id: "createdBy",
      accessor: "createdBy",
      isVisible: true,
    },
    {
      Header: "UPLOADED_DATE",
      id: "createdAt",
      accessor: "createdAt",
      isVisible: true,
    },
    {
      Header: "MEDIA_SIZE",
      id: "size",
      accessor: "size",
      isVisible: true,
      width: 90,
    },
    {
      Header: '',
      id: 'actions',
      isVisible: true,
      
      mWidth: "mWidth-125",
      Cell: ({ row }) => (
        <div className="actions d-flex align-items-center">
          <button
            className="btn"
            id="editBtn"
            data-bs-toggle="modal"
            data-bs-target="#editFilesModal"
            onClick={(e) => {
              e.stopPropagation();
              if (handleEditClick) handleEditClick(e, row.original.id);
            }}
          >
          <img src={editIcon} className="actionIcon editAction" alt={<FormattedMessage id="EDIT" />} />
          </button>
          <span className="vertical-divider"></span>
          <button
            className="btn red"
            id="deleteBtns"
            data-bs-toggle="modal"
            data-bs-target="#notificationModal"
            onClick={handleDeleteConfirm.bind(null, [row.original.id])/*(e) => handleDeleteConfirm([row.original.id], e)*/}
          >
            <img src={trashIcon} className="actionIcon deleteAction" alt={<FormattedMessage id="DELETE" />} />
          </button>
        </div>
      ),
    },
  ];
  const isDisabled = checkedKey.length !== 1 || Object.keys(selectedIds).length === 0;
  const buttons = {
    toolbarRightButtons: (
      <>
        <button
          id="addFilesBtn"
          type="button"
          className="btn btn-primary but-darkblue fw-normal btn-primary-style"
          data-bs-toggle="modal"
          data-bs-target="#addFilesModal"
          disabled={checkedKey.length !==1 }
        >
          <FormattedMessage id="ADD_FILES" defaultMessage="Add Files" />
        </button>
      </>
    ),
    footerButtons: (
      <>
        <button
          id="moveFeles"
          type="button"
          className="btn"
          data-bs-toggle="modal"
          data-bs-target="#notificationModal"
          disabled={isDisabled}
          onClick={handleMoveFilesConfirm.bind(null, Object.keys(selectedIds))}
        >
          <span>
            <FormattedMessage id="MOVE_FILES" />
          </span>
        </button>
        <button
          id="removeFiles"
          type="button"
          className="btn"
          data-bs-toggle="modal"
          data-bs-target="#notificationModal"
          disabled={Object.keys(selectedIds).length === 0}
          onClick={handleDeleteConfirm.bind(null, Object.keys(selectedIds))}
        >
          <span>
            <FormattedMessage id="REMOVE_FILES" />
          </span>
        </button>
      </>
    ),
  };

  const tableProps = {
    pageType: constants.pageType.MediaLibrary,
    modalId: "MediaLibrary",
    columns: tableColumnArray,
    data,
    setData,
    dataMap,
    generateDataSlice,
    sortMap,
    dataCount,
    setDataCount,
    totalDataCount,
    setSortBy,
    sortByCol,
    sortByAsc,
    success,
    setSuccess,
    error,
    setError,
    startIndex: startIndexForTable,
    selectedIds,
    setSelectedIds,
    searchValue,
    setSearchValue,
    tableRef,
    tableWrapperRef,
    dataLoaded,
    buttons,
    totalSize,
    selectedSize,
  };

  const folderTreeProps = {
    folderTreeData,
    checkedKey,
    setCheckedKey,
    selectedKey,
    setSelectedKey,
    handleDeleteFolder,
    triggerEditFolder,
    setError
  };

  const handleAddFile = async (files) => {
    //const fileWithFolder = { ...files, folderId: checkedKey[0] };
    try {
      let res = await API.uploadMediaFiles(checkedKey[0], files);
      if(res.status === 200){
        if (typeof fetchFolderTreeData === 'function') fetchFolderTreeData();
        if (typeof loadTable === 'function') loadTable(dataMap);
        setSuccess(<FormattedMessage id="UPLOAD_FILE_SUCCESS" />);
      }else{
        setError(<FormattedMessage id="UPLOAD_FILE_FAILED"  values={{ errorMsg: res.data.message }} />);
      }

    } catch (err) {
      setError(<FormattedMessage id="UPLOAD_FILE_FAILED" values={{ errorMsg: err.message }}/>);
    }
  };

  const checkStorageCapacityBySiteId = async (filesSize) => {
    const selectedFolderInfo =folderTreeData.find(folder => folder.key === checkedKey[0]);
    let res = await API.getCapacityBySite(selectedFolderInfo.siteId, selectedFolderInfo.cmsId);
    if(res.status === 200){
      // GB to Bytes
      if(res.data && filesSize > res.data.totalRemain * 1024 * 1024 * 1024){ 
        return false;
      }
    }
    return true;
  };

  const handleEditFile = async (id, data) => {
    await updateFile(id, data);
    if (typeof fetchFolderTreeData === 'function') fetchFolderTreeData();
    if (typeof loadTable === 'function') loadTable(dataMap);
    setSuccess(<FormattedMessage id="EDIT_FILE_SUCCESS" />);
  };

  const userModalProps = {
    user,
    success,
    error,
    setSuccess,
    setError,
    closeModal,
    loadTable,
    fetchFolderTreeData,
    dataMap,
    handleAddFile,
    checkStorageCapacityBySiteId, 
    selectedKey,
    checkedKey,
  };
  const editFilesProps = {
    user,
    selectedFiles,
    setSelectedFiles,
    success,
    error,
    setSuccess,
    setError,
    closeModal,
    loadTable,
    fetchFolderTreeData,
    dataMap,
    handleEditFile,
  };

  return (
    <>
      <EditFiles {...editFilesProps} />
      <NotificationModal {...notificationProps} />
      <AddFiles {...userModalProps} />
      <div className="d-flex flex-column flex-grow-1 h-100">
        <Banner />
        <div className="mes-title d-flex justify-content-between align-items-center">
          <FormattedMessage id="MediaLibrary" />
        </div>
        <div className="row h-100">
          <div className="col-3 h-100">
            <div
              className="card mt-auto tree-height me-3 mt-2 mb-5 flex-grow-1"
              style={{ borderRadius: "8px" }}
            >
              <div className="d-flex custom-size p-3 justify-content-between ">
                <h6 className="fw-semibold mb-0">
                  <FormattedMessage id="FOLDER" />
                </h6>
              </div>
              <FolderTree {...folderTreeProps} />
              <div className="mt-auto d-flex">
                <button
                  className="btn btn-primary add-group-button border-0"
                  data-bs-toggle="modal"
                  data-bs-target="#folderModal"
                >
                  <img src={add} alt="Send Icon" className="me-2" />
                  <FormattedMessage id="ADD_NEW_FOLDER" />
                </button>
              </div>

            </div>
          </div>
          <div className="col-9">
            <MesTable {...tableProps} />
          </div>
        </div>
        <AddNewFolder
          folderTreeData={folderTreeData}
          setSuccess={setSuccess}
          setError={setError}
          closeModal={closeModal}
          loadTable={loadTable}
          fetchFolderTreeData={fetchFolderTreeData}
          selectedFolder={selectedFolder}
          setSelectedFolder={setSelectedFolder}
          dataMap={dataMap}
        />
      </div>
    </>
  );
}

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps)(injectIntl(SignageMediaLibrary))
